'use client';

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import TimeAgo, { LocaleData } from 'javascript-time-ago';

type ContextType = {
  timeSince: (value: number) => string;
};

const DateTimesContext = createContext<ContextType | null>(null);

interface Props {
  children: ReactNode;
  timeAgoLocale: LocaleData;
}

const DateTimesProvider = ({ children, timeAgoLocale }: Props) => {
  const timeAgo = useRef(
    (() => {
      TimeAgo.addLocale(timeAgoLocale);
      return new TimeAgo(timeAgoLocale.locale);
    })()
  );

  const timeSince = useCallback(
    (value: number) => timeAgo.current.format(value, 'mini'),
    []
  );

  const value = useMemo(() => ({ timeSince }), [timeSince]);

  return (
    <DateTimesContext.Provider value={value}>
      {children}
    </DateTimesContext.Provider>
  );
};

function useDateTimes() {
  const context = useContext(DateTimesContext);
  if (!context) throw new Error('useDateTimes requires a context!');

  return context;
}

export { DateTimesProvider, useDateTimes };
